import type {
  BotGrowthMode,
  BotGrowthRule,
  BotPlayRhythm,
  BotProfile,
  BotProfileMetaRule,
  BotProfileStatRule,
  BotProfileWeightedValue,
  BotTimeBand,
  BotsConfig,
  BotsHelpKind,
  BotsHelpPayload,
  BotsLeaderboardSim,
  BotsScoreMode,
  BotsProfileConfig,
} from "./types";

/**
 * The game design document's own numbers, as the platform default. A project
 * that configures nothing gets exactly the behaviour that document describes —
 * which is the point of writing them down twice, here and in internal/bot.
 *
 * It is a function rather than a constant because the nested arrays would
 * otherwise be shared between every form on the page: editing one project's
 * profile table would silently edit the default the next one loads.
 */
export function DEFAULT_BOTS_CONFIG(): BotsConfig {
  return {
    enabled: false,
    // Absent means generated — the behaviour of every project that existed
    // before the roster table did.
    source: "generated",
    default_locale: "en",
    locale_by_country: {},
    population: {
      concentrate_real_players: true,
      fill_below_members: 25,
      target_members_min: 40,
      target_members: 40,
      max_bot_share: 60,
      join_probability: 3,
      leave_probability: 2,
      min_stay_hours: 24,
      min_rejoin_hours: 12,
      kick_refill_pause_hours: 24,
    },
    backfill: {
      enabled: true,
      depth_hours: 72,
      min_messages: 18,
      max_messages: 34,
    },
    help: { guaranteed_response: true, kinds: [] },
    newcomer_boost: {
      window_minutes: 60,
      speed_up: 2.5,
      welcome_probability: 75,
    },
    simulation: { awake_window_minutes: 60 },
    profiles: DEFAULT_BOT_PROFILES(),
    time_of_day: DEFAULT_TIME_OF_DAY(),
    guild_limits: {
      messages_per_hour: 12,
      gap_seconds: 60,
      no_repeat_last: 3,
      phrase_reuse_hours: 24,
    },
    chat: {
      reply_window_minutes: 5,
      max_reactions_per_2min: 1,
      thanks_probability: 75,
      thanks_delay_min_seconds: 30,
      thanks_delay_max_seconds: 300,
      small_talk_weight: 50,
    },
    names: { custom: {}, digits_suffix_percent: 30 },
    // Registered the moment the bot is created — byte for byte what every
    // project did before this key existed. A past is a fiction an operator
    // asks for explicitly, and the platform never invents one for them.
    account_age: { min_days: 0, max_days: 0 },
    growth: [],
    leaderboards: [],
    maturation: {
      leave_when_real_members: 25,
      sleep_when_active_players: 5000,
    },
    // No rules at all — a bot is created with a name and a country, and
    // nothing else: no card, and no avatar either, exactly as it was before
    // this section existed. The keys a project WOULD fill in are its own
    // schema's, and the platform never guesses them; the avatar it would wear
    // is its own `profile.allowed_avatars`, and the platform never picks one
    // unasked.
    profile: { avatar: [], stats: [], meta: [] },
  };
}

/** A play rhythm from its six ends, in the order the table reads them. */
function play(
  daysMin: number,
  daysMax: number,
  sessionsMin: number,
  sessionsMax: number,
  minutesMin: number,
  minutesMax: number,
): BotPlayRhythm {
  return {
    days_per_week_min: daysMin,
    days_per_week_max: daysMax,
    sessions_per_day_min: sessionsMin,
    sessions_per_day_max: sessionsMax,
    session_minutes_min: minutesMin,
    session_minutes_max: minutesMax,
  };
}

/** §3.1 of the design document, verbatim, each with the play rhythm
 *  internal/bot gives the same profile. The shares add up to 100. */
export function DEFAULT_BOT_PROFILES(): BotProfile[] {
  return [
    {
      key: "casual",
      share: 40,
      message_probability: 8,
      min_interval_minutes: 180,
      max_interval_minutes: 480,
      max_messages_per_day: 2,
      cooldown_min_minutes: 30,
      cooldown_max_minutes: 60,
      play: play(1, 3, 1, 2, 10, 30),
    },
    {
      key: "active",
      share: 35,
      message_probability: 15,
      min_interval_minutes: 120,
      max_interval_minutes: 300,
      max_messages_per_day: 5,
      cooldown_min_minutes: 20,
      cooldown_max_minutes: 40,
      play: play(5, 7, 2, 4, 20, 60),
    },
    {
      key: "social",
      share: 20,
      message_probability: 25,
      min_interval_minutes: 60,
      max_interval_minutes: 180,
      max_messages_per_day: 8,
      cooldown_min_minutes: 10,
      cooldown_max_minutes: 25,
      play: play(3, 6, 2, 5, 15, 40),
    },
    {
      key: "leader",
      share: 5,
      message_probability: 35,
      min_interval_minutes: 30,
      max_interval_minutes: 90,
      max_messages_per_day: 12,
      cooldown_min_minutes: 5,
      cooldown_max_minutes: 15,
      play: play(6, 7, 3, 5, 30, 90),
    },
  ];
}

/** §6.2 stage 2 — bands that tile the whole day. */
export function DEFAULT_TIME_OF_DAY(): BotTimeBand[] {
  return [
    { from: "00:00", to: "06:00", factor: 0.15 },
    { from: "06:00", to: "09:00", factor: 0.35 },
    { from: "09:00", to: "18:00", factor: 0.8 },
    { from: "18:00", to: "22:00", factor: 1.3 },
    { from: "22:00", to: "00:00", factor: 0.55 },
  ];
}

/** What a newly added help kind starts as — §8.2 and §9.2. */
export function defaultHelpKind(requestType: string): BotsHelpKind {
  return {
    request_type: requestType,
    weight: 25,
    interval_minutes: 180,
    max_helpers: 5,
    first_delay_min_seconds: 30,
    first_delay_max_seconds: 120,
    next_delay_min_seconds: 180,
    next_delay_max_seconds: 1200,
    donate_amount: 1,
    // No `payloads` key at all: the bot asks with no payload, which is what
    // every kind of every project did before this list existed and what a
    // request type with no schema still accepts. Absent rather than `[]`
    // because the two say the same thing and only one of them shows up as a
    // change in a config nobody edited.
  };
}

/** A newly added board starts switched OFF: a bot's score takes a rank away
 *  from a real player, so nothing about it is opt-out. */
export function defaultLeaderboardSim(extKey: string): BotsLeaderboardSim {
  return {
    ext_key: extKey,
    enabled: false,
    submits_per_day_min: 1,
    submits_per_day_max: 3,
    score_per_submit_min: 100,
    score_per_submit_max: 500,
    score_mode: "result",
  };
}

/** A newly added growing stat: one level per 6–15 minutes of play, uncapped. */
export function defaultGrowthRule(key: string): BotGrowthRule {
  return {
    key,
    mode: "during_play",
    gain_min: 1,
    gain_max: 1,
    every_minutes_min: 6,
    every_minutes_max: 15,
  };
}

/**
 * The document a save sends: a per-day row without its interval, and a board
 * that follows a stat without the numbers and mode it no longer uses — the
 * draft keeps both so switching back restores them.
 */
export function savedBots(bots: BotsConfig): BotsConfig {
  return {
    ...bots,
    growth: bots.growth.map((g) => {
      const out: Partial<BotGrowthRule> = { ...g };
      if (g.mode === "per_day") {
        delete out.every_minutes_min;
        delete out.every_minutes_max;
      }
      if (out.cap === undefined || !Number.isFinite(out.cap)) delete out.cap;
      return out as BotGrowthRule;
    }),
    leaderboards: bots.leaderboards.map((l) => {
      if (!l.score_stat) {
        const { score_stat: _unused, ...rest } = l;
        return rest;
      }
      return {
        ext_key: l.ext_key,
        enabled: l.enabled,
        score_stat: l.score_stat,
      } as BotsLeaderboardSim;
    }),
  };
}

function normalizeGrowth(input: unknown): BotGrowthRule[] {
  if (!Array.isArray(input)) return [];
  return input
    .filter((g): g is Record<string, unknown> => !!g && typeof g === "object")
    .map((g) => {
      const rule: BotGrowthRule = {
        key: str(g.key, ""),
        // Kept as written, so an unknown mode reaches the validator.
        mode: str(g.mode, "during_play") as BotGrowthMode,
        gain_min: num(g.gain_min, NaN),
        gain_max: num(g.gain_max, NaN),
        every_minutes_min: num(g.every_minutes_min, NaN),
        every_minutes_max: num(g.every_minutes_max, NaN),
      };
      const cap = optNum(g.cap);
      if (cap !== undefined) rule.cap = cap;
      return rule;
    });
}

/**
 * Coerce `bots.profile` into rules the editor can bind against.
 *
 * Every number here is OPTIONAL and stays optional: a key that is not in the
 * blob must not come back out of the normalizer as `0`, or the save bar would
 * report changes on a page nobody has edited — the same discipline the help
 * quota follows in `normalizeHelpTypes`. A rule that survives the walk with
 * nothing but its key says nothing at all and is dropped; so is a meta rule
 * with no value to draw from, which is indistinguishable from "this key has no
 * rule" once the junk is gone.
 *
 * Rules naming a key the project's schema no longer declares are KEPT. The
 * normalizer does not read the schema, and dropping them here would delete a
 * config the operator can still see in the JSON view; the editor simply has no
 * row to draw them on.
 *
 * Dropping the say-nothing rule is safe precisely because `validateBots`
 * refuses to save one: a ticked int key needs both bounds, a ticked bool key
 * needs its chance, a ticked pool needs a value. So the row an operator is in
 * the middle of filling in only ever exists in the form's own state, which
 * this walk does not touch — it runs on what the server answered, on a JSON
 * document being typed into the raw view, and on the config a save echoed
 * back. A rule with nothing but a key cannot have come from any of the three
 * unless somebody wrote it by hand, and there it says nothing at runtime
 * either.
 */
export function normalizeBotsProfile(input: unknown): BotsProfileConfig {
  const raw = (input ?? {}) as Record<string, unknown>;
  return {
    // The avatar is a plain weighted list — the same shape a string meta key's
    // pool has — and it is ALWAYS here, empty when the project has no rule.
    // A rule kept out of the walk would be dropped by it: the normalizer runs
    // on what the server answered and on the config a save echoed back, so a
    // key it does not read is a key that never survives a round trip.
    avatar: weightedValues(raw.avatar),
    stats: normalizeStatRules(raw.stats),
    meta: normalizeMetaRules(raw.meta),
  };
}

function normalizeStatRules(input: unknown): BotProfileStatRule[] {
  if (!Array.isArray(input)) return [];
  const out: BotProfileStatRule[] = [];
  for (const raw of input) {
    if (!raw || typeof raw !== "object") continue;
    const entry = raw as Record<string, unknown>;
    if (typeof entry.key !== "string" || !entry.key) continue;
    const rule: BotProfileStatRule = { key: entry.key };
    // A weighted pool is a stat's form too — `string` keys are drawn from one,
    // exactly as a meta string is.
    const values = weightedValues(entry.values);
    if (values.length > 0) rule.values = values;
    for (const field of [
      "start_min",
      "start_max",
      "true_percent",
    ] as const) {
      const n = optNum(entry[field]);
      if (n !== undefined) rule[field] = n;
    }
    // Nothing but a key: no range, no percentage, no pool.
    if (Object.keys(rule).length > 1) out.push(rule);
  }
  return out;
}

function normalizeMetaRules(input: unknown): BotProfileMetaRule[] {
  if (!Array.isArray(input)) return [];
  const out: BotProfileMetaRule[] = [];
  for (const raw of input) {
    if (!raw || typeof raw !== "object") continue;
    const entry = raw as Record<string, unknown>;
    if (typeof entry.key !== "string" || !entry.key) continue;
    const rule: BotProfileMetaRule = { key: entry.key };
    const values = weightedValues(entry.values);
    // The list is absent, not empty, on the numeric forms: `"values": []` on an
    // int key is dead config the next reader would puzzle over.
    if (values.length > 0) rule.values = values;
    for (const field of [
      "count_min",
      "count_max",
      "start_min",
      "start_max",
      "true_percent",
    ] as const) {
      const n = optNum(entry[field]);
      if (n !== undefined) rule[field] = n;
    }
    // Nothing but a key: no pool, no range, no chance.
    if (Object.keys(rule).length > 1) out.push(rule);
  }
  return out;
}

/** A candidate is a string and a number, or it is junk. */
function weightedValues(input: unknown): BotProfileWeightedValue[] {
  if (!Array.isArray(input)) return [];
  return input
    .filter((v): v is Record<string, unknown> => !!v && typeof v === "object")
    .filter((v) => typeof v.value === "string" && optNum(v.weight) !== undefined)
    .map((v) => ({ value: v.value as string, weight: v.weight as number }));
}

/** A number that is really there — or a key that stays absent. */
function optNum(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

/**
 * Coerce one kind's `payloads`, as the fragment to spread into the kind — so a
 * kind with no readable template comes back out without the key, exactly as it
 * went in. Absent and empty mean the same thing to the simulation, and only one
 * of them is a diff on a config nobody edited.
 *
 * A template with no weight is drawn at 1 — the server's own default, and the
 * simple case the editor names out loud: one template and no weights is chosen
 * every time. A template whose `payload` is not an object is DROPPED rather
 * than kept as an unsaveable row: the server drops it too, and a row nobody can
 * fix by editing it (there is nothing in the box to edit) would block Save for
 * good.
 */
function normalizeHelpPayloads(input: unknown): { payloads?: BotsHelpPayload[] } {
  if (!Array.isArray(input)) return {};
  const out: BotsHelpPayload[] = [];
  for (const raw of input) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
    const entry = raw as Partial<BotsHelpPayload>;
    const payload = entry.payload;
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) continue;
    out.push({ weight: num(entry.weight, 1), payload });
  }
  return out.length > 0 ? { payloads: out } : {};
}

/**
 * A row's play rhythm as stored, and nothing more: a row without one stays
 * without one and an absent end reads as NaN, so the validator refuses the gap
 * instead of the form quietly saving a rhythm nobody chose.
 */
function normalizePlay(input: unknown): { play?: BotPlayRhythm } {
  if (!input || typeof input !== "object" || Array.isArray(input)) return {};
  const p = input as Record<string, unknown>;
  return {
    play: {
      days_per_week_min: num(p.days_per_week_min, NaN),
      days_per_week_max: num(p.days_per_week_max, NaN),
      sessions_per_day_min: num(p.sessions_per_day_min, NaN),
      sessions_per_day_max: num(p.sessions_per_day_max, NaN),
      session_minutes_min: num(p.session_minutes_min, NaN),
      session_minutes_max: num(p.session_minutes_max, NaN),
    },
  };
}

function num(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

function bool(v: unknown, fallback: boolean): boolean {
  return typeof v === "boolean" ? v : fallback;
}

function str(v: unknown, fallback: string): string {
  return typeof v === "string" ? v : fallback;
}

/**
 * Coerce an unknown blob into a BotsConfig.
 *
 * The section is large and every part of it is optional, so the walk is long
 * but shallow: each field falls back to the platform default, and the two
 * tables fall back to the document's own values when they are missing
 * entirely — never when they are present and merely wrong, since an operator
 * editing a table has to see what they typed.
 */
export function normalizeBots(input: unknown): BotsConfig {
  const d = DEFAULT_BOTS_CONFIG();
  const raw = (input ?? {}) as Record<string, unknown>;
  const pop = (raw.population ?? {}) as Record<string, unknown>;
  const target = num(pop.target_members, d.population.target_members);
  const back = (raw.backfill ?? {}) as Record<string, unknown>;
  const help = (raw.help ?? {}) as Record<string, unknown>;
  const newcomer = (raw.newcomer_boost ?? {}) as Record<string, unknown>;
  const sim = (raw.simulation ?? {}) as Record<string, unknown>;
  const limits = (raw.guild_limits ?? {}) as Record<string, unknown>;
  const chat = (raw.chat ?? {}) as Record<string, unknown>;
  const names = (raw.names ?? {}) as Record<string, unknown>;
  const age = (raw.account_age ?? {}) as Record<string, unknown>;
  const mat = (raw.maturation ?? {}) as Record<string, unknown>;

  const localeByCountry: Record<string, string> = {};
  if (raw.locale_by_country && typeof raw.locale_by_country === "object") {
    for (const [k, v] of Object.entries(
      raw.locale_by_country as Record<string, unknown>,
    )) {
      if (typeof v === "string") localeByCountry[k.toUpperCase()] = v.toLowerCase();
    }
  }
  const customNames: Record<string, string[]> = {};
  if (names.custom && typeof names.custom === "object") {
    for (const [k, v] of Object.entries(names.custom as Record<string, unknown>)) {
      if (Array.isArray(v)) {
        customNames[k.toLowerCase()] = v.filter(
          (n): n is string => typeof n === "string",
        );
      }
    }
  }

  return {
    enabled: bool(raw.enabled, d.enabled),
    // Anything that is not the one other spelling reads as the default: an
    // unknown source is a config the server refuses anyway, and guessing at it
    // here would only decide on the operator's behalf which of two very
    // different behaviours a broken value meant.
    source: raw.source === "table" ? "table" : d.source,
    default_locale: str(raw.default_locale, d.default_locale),
    locale_by_country: localeByCountry,
    population: {
      concentrate_real_players: bool(
        pop.concentrate_real_players,
        d.population.concentrate_real_players,
      ),
      fill_below_members: num(pop.fill_below_members, d.population.fill_below_members),
      // The lower end falls back to the upper one rather than to the platform
      // default, exactly as parsePopulation does on the server: a document
      // written before the range existed names one target, and reading the
      // other end as 40 would spread guilds a project never asked to spread.
      target_members_min: num(pop.target_members_min, target),
      target_members: target,
      max_bot_share: num(pop.max_bot_share, d.population.max_bot_share),
      join_probability: num(pop.join_probability, d.population.join_probability),
      leave_probability: num(pop.leave_probability, d.population.leave_probability),
      min_stay_hours: num(pop.min_stay_hours, d.population.min_stay_hours),
      min_rejoin_hours: num(pop.min_rejoin_hours, d.population.min_rejoin_hours),
      kick_refill_pause_hours: num(
        pop.kick_refill_pause_hours,
        d.population.kick_refill_pause_hours,
      ),
    },
    backfill: {
      enabled: bool(back.enabled, d.backfill.enabled),
      depth_hours: num(back.depth_hours, d.backfill.depth_hours),
      min_messages: num(back.min_messages, d.backfill.min_messages),
      max_messages: num(back.max_messages, d.backfill.max_messages),
    },
    help: {
      guaranteed_response: bool(
        help.guaranteed_response,
        d.help.guaranteed_response,
      ),
      kinds: Array.isArray(help.kinds)
        ? help.kinds
            .filter((k): k is Record<string, unknown> => !!k && typeof k === "object")
            .map((k) => {
              const base = defaultHelpKind(str(k.request_type, ""));
              return {
                ...base,
                weight: num(k.weight, base.weight),
                interval_minutes: num(k.interval_minutes, base.interval_minutes),
                max_helpers: num(k.max_helpers, base.max_helpers),
                first_delay_min_seconds: num(
                  k.first_delay_min_seconds,
                  base.first_delay_min_seconds,
                ),
                first_delay_max_seconds: num(
                  k.first_delay_max_seconds,
                  base.first_delay_max_seconds,
                ),
                next_delay_min_seconds: num(
                  k.next_delay_min_seconds,
                  base.next_delay_min_seconds,
                ),
                next_delay_max_seconds: num(
                  k.next_delay_max_seconds,
                  base.next_delay_max_seconds,
                ),
                donate_amount: num(k.donate_amount, base.donate_amount),
                ...normalizeHelpPayloads(k.payloads),
              };
            })
        : [],
    },
    newcomer_boost: {
      window_minutes: num(newcomer.window_minutes, d.newcomer_boost.window_minutes),
      speed_up: num(newcomer.speed_up, d.newcomer_boost.speed_up),
      welcome_probability: num(
        newcomer.welcome_probability,
        d.newcomer_boost.welcome_probability,
      ),
    },
    simulation: {
      awake_window_minutes: num(
        sim.awake_window_minutes,
        d.simulation.awake_window_minutes,
      ),
    },
    profiles: Array.isArray(raw.profiles) && raw.profiles.length > 0
      ? raw.profiles
          .filter((p): p is Record<string, unknown> => !!p && typeof p === "object")
          .map((p) => ({
            key: str(p.key, ""),
            share: num(p.share, 0),
            message_probability: num(p.message_probability, 0),
            min_interval_minutes: num(p.min_interval_minutes, 60),
            max_interval_minutes: num(p.max_interval_minutes, 180),
            max_messages_per_day: num(p.max_messages_per_day, 0),
            cooldown_min_minutes: num(p.cooldown_min_minutes, 10),
            cooldown_max_minutes: num(p.cooldown_max_minutes, 30),
            ...normalizePlay(p.play),
          }))
      : DEFAULT_BOT_PROFILES(),
    time_of_day:
      Array.isArray(raw.time_of_day) && raw.time_of_day.length > 0
        ? raw.time_of_day
            .filter((b): b is Record<string, unknown> => !!b && typeof b === "object")
            .map((b) => ({
              from: str(b.from, "00:00"),
              to: str(b.to, "00:00"),
              factor: num(b.factor, 1),
            }))
        : DEFAULT_TIME_OF_DAY(),
    guild_limits: {
      messages_per_hour: num(
        limits.messages_per_hour,
        d.guild_limits.messages_per_hour,
      ),
      gap_seconds: num(limits.gap_seconds, d.guild_limits.gap_seconds),
      no_repeat_last: num(limits.no_repeat_last, d.guild_limits.no_repeat_last),
      phrase_reuse_hours: num(
        limits.phrase_reuse_hours,
        d.guild_limits.phrase_reuse_hours,
      ),
    },
    chat: {
      reply_window_minutes: num(
        chat.reply_window_minutes,
        d.chat.reply_window_minutes,
      ),
      max_reactions_per_2min: num(
        chat.max_reactions_per_2min,
        d.chat.max_reactions_per_2min,
      ),
      thanks_probability: num(chat.thanks_probability, d.chat.thanks_probability),
      thanks_delay_min_seconds: num(
        chat.thanks_delay_min_seconds,
        d.chat.thanks_delay_min_seconds,
      ),
      thanks_delay_max_seconds: num(
        chat.thanks_delay_max_seconds,
        d.chat.thanks_delay_max_seconds,
      ),
      small_talk_weight: num(chat.small_talk_weight, d.chat.small_talk_weight),
    },
    names: {
      custom: customNames,
      digits_suffix_percent: num(
        names.digits_suffix_percent,
        d.names.digits_suffix_percent,
      ),
    },
    // A blob written before this key existed falls back to 0 – 0, which is
    // exactly the behaviour that blob was saved under: those bots were
    // registered the moment they were created, and reading the absence as
    // anything else would re-date a project nobody has configured.
    account_age: {
      min_days: num(age.min_days, d.account_age.min_days),
      max_days: num(age.max_days, d.account_age.max_days),
    },
    growth: normalizeGrowth(raw.growth),
    leaderboards: Array.isArray(raw.leaderboards)
      ? raw.leaderboards
          .filter((l): l is Record<string, unknown> => !!l && typeof l === "object")
          .map((l) => {
            const base = defaultLeaderboardSim(str(l.ext_key, ""));
            return {
              ...base,
              enabled: bool(l.enabled, base.enabled),
              submits_per_day_min: num(
                l.submits_per_day_min,
                base.submits_per_day_min,
              ),
              submits_per_day_max: num(
                l.submits_per_day_max,
                base.submits_per_day_max,
              ),
              score_per_submit_min: num(
                l.score_per_submit_min,
                base.score_per_submit_min,
              ),
              score_per_submit_max: num(
                l.score_per_submit_max,
                base.score_per_submit_max,
              ),
              // Kept as written, so an unknown value reaches the validator
              // instead of being quietly turned into a result.
              score_mode: str(l.score_mode, base.score_mode) as BotsScoreMode,
              ...(typeof l.score_stat === "string" && l.score_stat !== ""
                ? { score_stat: l.score_stat }
                : {}),
            };
          })
      : [],
    maturation: {
      leave_when_real_members: num(
        mat.leave_when_real_members,
        d.maturation.leave_when_real_members,
      ),
      sleep_when_active_players: num(
        mat.sleep_when_active_players,
        d.maturation.sleep_when_active_players,
      ),
    },
    profile: normalizeBotsProfile(raw.profile),
  };
}
